import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Row } from 'antd';
import { uniqBy } from 'es-toolkit';
import moment from 'moment';

import { setAppLoader } from '@/redux/common/CommonActions';
import {
  getAllLocationsEffect,
  getAllNewUnitSizeIdEffect,
  getAllSupplyLocationIDEffect,
  getAllUnitRentalCompanyIdEffect,
  getTrailersListEffect,
  saveTrailer,
  updateTrailerNote,
} from '@/redux/Savers/SaversEffects';
import AlertModal from '@/views/components/Modals/AlertModal';
import { TRAILER_STATUS_CODE } from '@/views/pages/Savers/TrailerManagement/constants';
import { DEFAULT_PARAMS, excludedStatuses } from '@/views/pages/Savers/trailer-request.constants';

import TrailerActions from './TrailerActions';
import TrailerDetailsForm from './TrailerDetailsForm';
import TrailerFilter from './TrailerFilter';
import TrailerIssueForm from './TrailerIssueForm';
import { buildTrailerPayload } from './utils';

const TrailerSearch = ({ branchKey, onGeneralSearch, onSearchClick, isModal, status }) => {
  const dispatch = useDispatch();

  const [formNewTrailer] = Form.useForm();
  const [formIssueTrailer] = Form.useForm();
  const [hidden, setHidden] = useState(false);
  const [issueHidden, setIssueHidden] = useState(false);
  const [trailerNumberOptions, setTrailerNumberOptions] = useState([]);
  const [trailerNumber, setTrailerNumber] = useState(0);

  const [unitRentalCompanyIdOptions, setUnitRentalCompanyIdOptions] = useState([]);
  const [unitSizeIdOptions, setUnitSizeIdOptions] = useState([]);

  const { selectedCompany } = useSelector((state) => state.auth);

  const [selectedTrailerNote, setSelectedTrailerNote] = useState('');
  const [isOutOfService, setIsOutOfService] = useState(false);
  const [selectedTrailerIssueData, setSelectedTrailerIssueData] = useState('');
  const [trailerLocationIdOptions, setTrailerLocationIdOptions] = useState([]);
  const [searchStatusId, setSearchStatusId] = useState('');
  const [searchTrailerId, setSearchTrailerId] = useState('');
  const [supplyZone, setSupplyZone] = useState('');
  const [searchLocationId, setSearchLocationId] = useState(branchKey);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [supplyZoneNumberOptions, setSupplyZoneNumberOptions] = useState([]);
  const [checkOptions, setCheckOptions] = useState({
    rollDoor: false,
  });
  const [rentalStatus, setRentalStatus] = useState(true);
  const [trailerFormMode, setTrailerFormMode] = useState('add');

  // biome-ignore lint/correctness/useExhaustiveDependencies: unwanted dependencies
  useEffect(() => {
    getTrailerList();
    getAllTrailerLocationID();
    getAllUnitRentalCompanyID();
    getAllUnitSizeID();
    getAllDropDownOptions();
  }, [selectedCompany]);

  const onTrailerNumberInput = async (e) => {
    try {
      dispatch(setAppLoader(true));
      const trailerNumber = e.value;
      const data = await getTrailerDataByNumber(trailerNumber);
      const trailerData = data[0];
      formNewTrailer.setFieldsValue({
        inspectionExpDate: trailerData.inspectionExpDate ? moment(trailerData.inspectionExpDate) : null,
        locationID: trailerData.locationId,
        rentalRemovalDate: trailerData.rentalRemovalDate ? moment(trailerData.rentalRemovalDate) : null,
        rentalStatus: Boolean(trailerData.rentalStatus),
        rentalTrailerDate: trailerData.rentalTrailerDate ? moment(trailerData.rentalTrailerDate) : null,
        rollDoor: trailerData.rollDoor,
        status: trailerData.status.trim(),
        unitRentalCompanyID: trailerData.unitRentalCompanyID,
        unitSizeID: trailerData.unitSizeID,
      });
      setRentalStatus(Boolean(trailerData.rentalStatus));
      setSearchStatusId(trailerData.status.trim());
      dispatch(setAppLoader(false));
    } catch (error) {
      console.error('Error fetching trailer data:', error);
      dispatch(setAppLoader(false));
    }
  };

  const onChangeGeneralSearch = (e) => {
    onGeneralSearch(e.target.value);
  };

  useEffect(() => {
    if (selectedTrailerIssueData && selectedTrailerIssueData.length > 0) {
      setSelectedTrailerNote(selectedTrailerIssueData[0].trailerNotes);
    }
  }, [selectedTrailerIssueData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: unwanted dependencies
  useEffect(() => {
    if (status) {
      if (statusOptions) {
        const code = status === 'Loaded' ? TRAILER_STATUS_CODE.Loaded : TRAILER_STATUS_CODE.Empty;
        setSearchStatusId(code);
      }
    }
  }, []);

  const getAllDropDownOptions = async () => {
    try {
      const { data } = await getAllLocationsEffect();
      const supplyOptions = uniqBy(
        data.filter((row) => row.locationType && row.regionDesc),
        (row) => `${row.locationType}-${row.regionDesc}`,
      ).map((row) => ({
        label: `${row.locationType} (${row.regionDesc})`,
        value: row.locationType,
      }));

      setSupplyZoneNumberOptions(supplyOptions);
    } catch (error) {
      console.error(error);
    }
  };

  const onTrailerIdSelectGetIssue = async (e) => {
    try {
      const trailerId = e?.trailerId || '';
      if (trailerId) {
        const response = await getTrailerIssueDataById(trailerId);
        formIssueTrailer.setFieldsValue({
          issuetoReport: response[0].notInUseReason,
          notused: response[0].isOutOfService,
        });
        setTrailerNumber(response[0].trailerNumber);
        setIsOutOfService(response[0].isOutOfService);
        setSelectedTrailerNote(response[0].notInUseReason);
        setSelectedTrailerIssueData(response);
      } else {
        formIssueTrailer.setFieldsValue({
          issuetoReport: '',
          notused: '',
        });
        setTrailerNumber(0);
        setIsOutOfService('');
        setSelectedTrailerNote('');
      }
    } catch (error) {
      console.error('Error fetching trailer status data:', error);
    }
  };

  const onSelectFilterByTrailerNumber = (e) => {
    if (e) {
      setSearchTrailerId(e.value);
      onSearchClick(searchLocationId, e.value, searchStatusId, supplyZone, 'reset');
    } else {
      setSearchTrailerId('');
      onSearchClick(searchLocationId, '', searchStatusId, supplyZone, 'reset');
    }
  };

  const onSelectFilterBySupplyZone = (e) => {
    if (e) {
      setSupplyZone(e.value);
      onSearchClick(searchLocationId, searchTrailerId, searchStatusId, e.value, 'reset');
    } else {
      setSupplyZone('');
      onSearchClick(searchLocationId, searchTrailerId, searchStatusId, '', 'reset');
    }
  };

  const onSelectFilterByStatus = (e) => {
    if (e) {
      setSearchStatusId(e.value);
      onSearchClick(searchLocationId, searchTrailerId, e.value, supplyZone, 'reset');
    } else {
      setSearchStatusId('');
      onSearchClick(searchLocationId, searchTrailerId, '', supplyZone, 'reset');
    }
  };

  const onTrailerNoteInput = (e) => {
    setSelectedTrailerNote(e.target.value);
  };
  const handleChange = () => {
    setIsOutOfService(!isOutOfService);
  };

  const onTrailerLocationSearch = (e) => {
    const locationId = e?.value || '';
    if (locationId) {
      setSearchLocationId(locationId);
      onSearchClick(locationId, searchTrailerId, searchStatusId, supplyZone, 'reset');
    } else {
      setSearchLocationId('');
      onSearchClick(locationId, searchTrailerId, searchStatusId, supplyZone, 'reset');
    }
  };

  const closeModal = () => {
    formNewTrailer.resetFields();
    setHidden(!hidden);
  };

  const closeIssueModal = () => {
    formIssueTrailer.resetFields();
    setSelectedTrailerNote('');
    setIssueHidden(!issueHidden);
  };

  const openTrailerPopup = (type) => {
    setTrailerFormMode(type);
    setHidden(true);
  };

  const openReportPopup = () => {
    setIssueHidden(true);
  };

  const getTrailerList = async () => {
    try {
      let requestParam = {};
      if (searchLocationId) {
        requestParam = { branchKey: searchLocationId };
      }
      const { data } = await getTrailersListEffect(requestParam);
      const options = (
        isModal ? data.filter((row) => !row.isOutOfService && !excludedStatuses.includes(row.trailerStatusID)) : data
      ).reduce((acc, row) => {
        if (row.trailerNumber && row.trailerID && !acc.some((item) => item.value === row.trailerID)) {
          acc.push({ label: row.trailerNumber, trailerId: row.trailerID, value: row.trailerNumber });
        }
        return acc;
      }, []);
      setTrailerNumberOptions(options);
    } catch (error) {
      console.error(error);
      setTrailerNumberOptions([]);
    }
  };

  const getTrailerDataByNumber = async (trailerNumber) => {
    try {
      const { data } = await getTrailersListEffect({ trailerNumber, ...DEFAULT_PARAMS });
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const getTrailerIssueDataById = async (trailerId) => {
    try {
      const { data } = await getTrailersListEffect({ trailerId, ...DEFAULT_PARAMS });

      setSelectedTrailerIssueData(data);
      return data;
    } catch (error) {
      console.error(error);
      setSelectedTrailerIssueData([]);
    }
  };

  const getAllTrailerLocationID = async () => {
    try {
      const { data } = await getAllSupplyLocationIDEffect();

      const options = data
        .filter((row) => row.branchKey !== -1)
        .map((row) => ({
          label: row.branchDesc,
          value: row.branchKey,
        }));

      setTrailerLocationIdOptions(options);
    } catch (error) {
      console.error(error);
      setTrailerLocationIdOptions([]);
    }
  };

  const getAllUnitRentalCompanyID = async () => {
    try {
      const { data } = await getAllUnitRentalCompanyIdEffect();

      const options = data
        .filter((row) => row.id !== -1)
        .map((row) => ({
          label: row.name,
          value: row.id,
        }));
      setUnitRentalCompanyIdOptions(options);
    } catch (error) {
      console.error(error);
      setUnitRentalCompanyIdOptions([]);
    }
  };

  const getAllUnitSizeID = async () => {
    try {
      const { data } = await getAllNewUnitSizeIdEffect();

      const options = data
        .filter((row) => row.id !== -1)
        .map((row) => ({
          label: row.size,
          value: row.id,
        }));
      setUnitSizeIdOptions(options);
    } catch (error) {
      console.error(error);
      setUnitSizeIdOptions([]);
    }
  };

  const handleAddTrailer = async (formData) => {
    dispatch(setAppLoader(true));
    const data = buildTrailerPayload(formData);
    try {
      const response = await saveTrailer([data]);
      if (response) {
        dispatch(setAppLoader(false));
        const successMessage =
          trailerFormMode === 'add' ? 'Trailer added successfully' : 'Trailer updated successfully';
        setAlertMessage(successMessage);
        setAlertOpen(true);
        formNewTrailer.resetFields();
        getTrailerList();
        setHidden(false);
        onSearchClick(searchLocationId, searchTrailerId, searchStatusId, supplyZone, 'reset');
      }
    } catch (error) {
      console.error(error);
      dispatch(setAppLoader(false));
      const errorMessage =
        error?.response?.data ||
        error?.message ||
        (trailerFormMode === 'add' ? 'Unable to add the trailer.' : 'Unable to update the trailer.');

      setAlertMessage(errorMessage);
      setAlertOpen(true);
    }
  };

  const handleAlertOk = () => {
    setAlertOpen(false);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleUpdateTrailerNote = async () => {
    try {
      if (!trailerNumber || (isOutOfService && !selectedTrailerNote)) {
        setAlertOpen(!alertOpen);
        setAlertMessage('All the fields are required');
        return;
      }
      const data = [
        {
          isOutOfService,
          notInUseReason: selectedTrailerNote,
          trailerNumber: String(trailerNumber || ''),
        },
      ];
      await updateTrailerNote(data);
      setAlertMessage('Trailer Issue saved successfully');
      setAlertOpen(true);

      formIssueTrailer.resetFields();
      setSelectedTrailerNote('');
      setIsOutOfService(false);
      setIssueHidden(false);
      setTrailerNumber(0);
      onSearchClick(searchLocationId, searchTrailerId, searchStatusId, supplyZone, 'reset');
    } catch (error) {
      console.error('Trailer Issue save failed:', error);
    }
  };

  const handleOptionToggle = (e) => {
    const { name } = e.target;
    setCheckOptions((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleRentalStatusToggle = () => {
    formNewTrailer.setFieldValue('rentalStatus', !rentalStatus);
    setRentalStatus(!rentalStatus);
  };

  return (
    <>
      <AlertModal
        title="Alert"
        open={alertOpen}
        description={alertMessage}
        onOk={handleAlertOk}
        onClose={handleAlertClose}
      />
      <TrailerDetailsForm
        checkOptions={checkOptions}
        hidden={hidden}
        closeModal={closeModal}
        formNewTrailer={formNewTrailer}
        handleAddTrailer={handleAddTrailer}
        onTrailerNumberInput={onTrailerNumberInput}
        unitRentalCompanyIdOptions={unitRentalCompanyIdOptions}
        unitSizeIdOptions={unitSizeIdOptions}
        handleOptionToggle={handleOptionToggle}
        trailerFormMode={trailerFormMode}
        trailerNumberOptions={trailerNumberOptions}
        trailerLocationIdOptions={trailerLocationIdOptions}
        searchStatusId={searchStatusId}
        setSearchStatusId={setSearchStatusId}
        handleRentalStatusToggle={handleRentalStatusToggle}
        rentalStatus={rentalStatus}
      />

      <TrailerIssueForm
        issueHidden={issueHidden}
        CloseIssueModal={closeIssueModal}
        formIssueTrailer={formIssueTrailer}
        trailerNumberOptions={trailerNumberOptions}
        onTrailerIdSelectGetIssue={onTrailerIdSelectGetIssue}
        isOutOfService={isOutOfService}
        handleChange={handleChange}
        onTrailerNoteInput={onTrailerNoteInput}
        handleUpdateTrailerNote={handleUpdateTrailerNote}
      />

      <Row gutter={20}>
        <TrailerActions
          isModal={isModal}
          OpenReportPopup={openReportPopup}
          OpenTrailerPopup={openTrailerPopup}
          OpenUpdateTrailerPopup={openTrailerPopup}
        />
        <TrailerFilter
          onTrailerLocationSearch={onTrailerLocationSearch}
          onSelectFilterByStatus={onSelectFilterByStatus}
          onSelectFilterByTrailerNumber={onSelectFilterByTrailerNumber}
          onSelectFilterBySupplyZone={onSelectFilterBySupplyZone}
          onChangeGeneralSearch={onChangeGeneralSearch}
          searchLocationId={searchLocationId}
          status={status}
          supplyZoneNumberOptions={supplyZoneNumberOptions}
        />
      </Row>
    </>
  );
};

export default TrailerSearch;
